import React from 'react';
import ModalShell from './ModalShell';
import Button from './Button';
import EmptyState from './EmptyState';
import {
  RecentActivityItem,
  ActivityStatus,
  defaultActivityIcon,
} from './RecentActivitySidebar';
import { toneClasses } from '../lib/tones';
import { Icon } from './Icon';

// `tones.ts` has no slot for this 10% wash behind the icon, so these stay
// literal. `info` reuses `running`'s blue, as in the sidebar.
const STATUS_DOT_COLOR: Record<ActivityStatus, string> = {
  success: 'bg-emerald-500/10 text-emerald-500',
  error: 'bg-red-500/10 text-red-500',
  warning: 'bg-amber-500/10 text-amber-500',
  info: 'bg-blue-500/10 text-blue-500',
  running: 'bg-blue-500/10 text-blue-500',
  neutral: 'bg-slate-500/10 text-slate-500 dark:text-slate-400',
};

const STATUS_LABEL_COLOR: Record<ActivityStatus, string> = {
  success: toneClasses.success.value,
  error: toneClasses.danger.value,
  warning: toneClasses.warning.value,
  info: toneClasses.info.value,
  running: toneClasses.info.value,
  neutral: 'text-slate-500 dark:text-slate-400',
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  items: RecentActivityItem[];
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  loading?: boolean;
  emptyLabel?: string;
  /** Optional filter bar rendered above the list. */
  filters?: React.ReactNode;
  /** Locale for date/time formatting. */
  locale?: string;
  /** IANA timezone to render timestamps in. Omit → viewer's browser timezone. */
  timeZone?: string;
  /** Label for the pagination summary, e.g. "Mostrando {shown} de {total}". */
  paginationSummary?: string;
}

function formatDateTime(ts: string | Date | undefined, locale: string, timeZone?: string) {
  if (!ts) return '';
  const d = typeof ts === 'string' ? new Date(ts) : ts;
  return d.toLocaleString(locale, timeZone ? { timeZone } : undefined);
}

/**
 * Generic paginated history modal for activity/task feeds.
 *
 * Standard component used by both admin and tenant to browse the full history
 * behind the sidebar `RecentActivitySidebar`. Rendering, layout, pagination and
 * empty/loading states live here; each app just supplies the data and an
 * optional filter bar.
 *
 * Uses the same `RecentActivityItem` shape as the sidebar so visual parity is
 * automatic.
 */
export default function ActivityHistoryModal({
  isOpen,
  onClose,
  title,
  subtitle,
  items,
  total,
  page,
  pageSize,
  onPageChange,
  loading = false,
  emptyLabel = 'Sin registros',
  filters,
  locale = 'es-ES',
  timeZone,
  paginationSummary,
}: Props) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const computedSubtitle = subtitle ?? `${total} registros`;
  const summary = paginationSummary ?? `Mostrando ${items.length} de ${total}`;

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      subtitle={computedSubtitle}
      maxWidth="max-w-4xl"
      zIndex="z-[100]"
      headerActions={null}
    >
      {filters ? (
        <div className="mb-4 flex flex-wrap items-center gap-3">{filters}</div>
      ) : null}

      <div className="min-h-[300px]">
        {loading && items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <Icon name="progress_activity" className="animate-spin !text-[36px] text-primary" />
          </div>
        ) : items.length === 0 ? (
          <EmptyState icon="history" label={emptyLabel} />
        ) : (
          <div className="grid gap-3">
            {items.map((item) => {
              const status: ActivityStatus = item.status ?? 'neutral';
              const badge = STATUS_DOT_COLOR[status];
              const labelColor = STATUS_LABEL_COLOR[status];
              const Wrapper: React.ElementType = item.onClick ? 'button' : 'div';
              const iconNode = item.icon ?? defaultActivityIcon(status);
              return (
                <Wrapper
                  key={item.id}
                  onClick={item.onClick}
                  className="w-full text-left flex items-center justify-between p-4 rounded-xl bg-white dark:bg-surface-dark border border-border-subtle dark:border-border-subtle-dark hover:border-primary/40 transition-all group"
                >
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className={`p-2.5 rounded-xl flex items-center justify-center ${badge}`}>
                      {iconNode}
                    </div>
                    <div className="min-w-0">
                      <h4 className="text-sm font-bold text-slate-800 dark:text-slate-100 group-hover:text-primary transition-colors truncate">
                        {item.title}
                      </h4>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[10px] text-slate-500 dark:text-slate-500">
                          ID: #{item.id}
                        </span>
                        {item.timestamp ? (
                          <>
                            <span className="text-[10px] text-slate-400 dark:text-slate-600">•</span>
                            <span className="text-[10px] text-slate-500 dark:text-slate-500">
                              {formatDateTime(item.timestamp, locale, timeZone)}
                            </span>
                          </>
                        ) : null}
                        {item.meta ? (
                          <>
                            <span className="text-[10px] text-slate-400 dark:text-slate-600">•</span>
                            <span className="text-[10px] text-slate-500 dark:text-slate-500 flex items-center gap-1 truncate">
                              <Icon name="person" className="!text-[12px]" />
                              {item.meta}
                            </span>
                          </>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  <div className="hidden sm:block text-right shrink-0 pl-4">
                    {item.subtitle ? (
                      <p className={`text-[10px] font-bold uppercase tracking-wider ${labelColor}`}>
                        {item.subtitle}
                      </p>
                    ) : null}
                  </div>
                </Wrapper>
              );
            })}
          </div>
        )}
      </div>

      <div className="mt-6 pt-4 border-t border-border-subtle dark:border-border-subtle-dark flex items-center justify-between">
        <p className="text-xs text-slate-500 dark:text-slate-500">{summary}</p>
        <div className="flex items-center gap-2">
          <Button
            disabled={page === 0 || loading}
            onClick={() => onPageChange(page - 1)}
            variant="ghost"
            size="sm"
          >
            <Icon name="chevron_left" className="!text-[18px]" />
          </Button>
          <div className="flex items-center gap-1 px-4">
            <span className="text-sm font-bold text-slate-800 dark:text-white">{page + 1}</span>
            <span className="text-sm text-slate-400 dark:text-slate-600">/</span>
            <span className="text-sm text-slate-500 dark:text-slate-500">{totalPages}</span>
          </div>
          <Button
            disabled={page >= totalPages - 1 || loading}
            onClick={() => onPageChange(page + 1)}
            variant="ghost"
            size="sm"
          >
            <Icon name="chevron_right" className="!text-[18px]" />
          </Button>
        </div>
      </div>
    </ModalShell>
  );
}
